/**
 * Authoritative building geometry model (Phase 7) — pure data.
 *
 * One PlanModel per Floor. Coordinates are integer plan units (see
 * units.ts), x east / y north, origin shared by every Floor of a project
 * (so floors stack for future elevations/3D).
 *
 *   PlanNode  — shared vertex; connected Walls reference the SAME node
 *   Wall      — centerline between two nodes + thickness (faces derived),
 *               optional explicit height, optional exterior override
 *   WallOpening — door/window/cased opening hosted by ONE wall segment
 *   RoomAnchor — binds an existing Room record to whichever enclosed
 *               region contains the anchor point (no visual_rooms)
 *
 * Everything else (faces, runs, regions, areas, exterior classification,
 * dimension labels) is DERIVED by the engine and never stored as truth.
 * Identity is stable UUIDs; deletion = archivedAt (Phase 9 reinterprets).
 */

export interface Point {
  x: number;
  y: number;
}

export interface PlanNode {
  id: string;
  floorId: string;
  x: number;
  y: number;
  /** Lineage: the entity this was duplicated from (null = original). */
  originId: string | null;
  archivedAt: string | null;
}

/**
 * 'wall' = physical construction. 'boundary' = LOGICAL room divider
 * (open-plan Kitchen | Living): zero thickness, participates in room
 * detection, but is never a building component — no openings, no exterior
 * classification, no wall quantities, no elevations/3D, no demolition.
 */
export type WallKind = "wall" | "boundary";

export interface Wall {
  id: string;
  floorId: string;
  kind: WallKind;
  startNodeId: string;
  endNodeId: string;
  /** Thickness in pu (faces are ± thickness/2 from the centerline). 0 for boundaries. */
  thickness: number;
  /** Explicit height in whole inches; null = floor default (then 96"). */
  heightInches: number | null;
  /** null = derive exterior/interior from topology; true/false = override. */
  exteriorOverride: boolean | null;
  /** Lineage: the entity this was duplicated from (null = original). */
  originId: string | null;
  notes: string;
  archivedAt: string | null;
}

export type OpeningKind = "door" | "window" | "opening";

export type DoorSwing = "left-in" | "left-out" | "right-in" | "right-out" | null;

/**
 * How many sashes share one rough opening (spec §60G). This is structural,
 * not decorative: the plan symbol draws the mullions, and replacing a double
 * window is two units of window, not one.
 */
export type WindowConfiguration = "single" | "double" | "triple";

export const WINDOW_CONFIGURATIONS: ReadonlyArray<{ id: WindowConfiguration; label: string; units: number }> = [
  { id: "single", label: "Single", units: 1 },
  { id: "double", label: "Double", units: 2 },
  { id: "triple", label: "Triple", units: 3 },
];

/**
 * How the window opens. Separate from the configuration because a double
 * casement and a double slider are both doubles, and because a user should
 * never be made to state both before they can draw one. "" = not stated.
 */
export type WindowOperation =
  | ""
  | "single-hung"
  | "double-hung"
  | "casement"
  | "sliding"
  | "picture"
  | "awning"
  | "other";

export const WINDOW_OPERATIONS: ReadonlyArray<{ id: WindowOperation; label: string }> = [
  { id: "", label: "Not stated" },
  { id: "single-hung", label: "Single-hung" },
  { id: "double-hung", label: "Double-hung" },
  { id: "casement", label: "Casement" },
  { id: "sliding", label: "Sliding" },
  { id: "picture", label: "Picture" },
  { id: "awning", label: "Awning" },
  { id: "other", label: "Other" },
];

/** How many window units one opening represents, for scope and estimating. */
export function windowUnitCount(configuration: WindowConfiguration | null | undefined): number {
  return WINDOW_CONFIGURATIONS.find((c) => c.id === (configuration ?? "single"))?.units ?? 1;
}

export interface WallOpening {
  id: string;
  wallId: string;
  kind: OpeningKind;
  /** Distance from the wall's START node to the opening's near edge (pu). */
  offsetFromStart: number;
  width: number;
  /** Height of the opening (pu). */
  height: number;
  /** Sill height above the floor (pu); 0 for doors. */
  sillHeight: number;
  swing: DoorSwing;
  /** Windows only: how many sashes share the opening. Doors are 'single'. */
  windowConfig: WindowConfiguration;
  /** Windows only: how it opens; "" when the user has not said. */
  windowOperation: WindowOperation;
  /** Lineage: the entity this was duplicated from (null = original). */
  originId: string | null;
  notes: string;
  archivedAt: string | null;
}

/**
 * PlanObject — a structured graphical building/design object (Phase 8):
 * fixtures, appliances, electrical devices, cabinet runs, countertops.
 * NOT decoration: stable UUID, design-scenario membership, root lineage
 * (origin_id), typed geometry, and an optional SCOPE BINDING (featureId →
 * a Project Feature whose quantity aggregates over its bound objects).
 *
 * Geometry types:
 *   point  — center (x,y) + rotation + width/depth footprint
 *   linear — run from (x,y) to (endX,endY) + depth (LF = run length)
 *   area   — run-shaped rectangle (length × depth = sqft); usually derived
 *            from a parent cabinet run until the user overrides (detached)
 *
 * Wall-hosted electrical (outlet/switch): hostWallId + hostOffset (pu from
 * the wall start) + hostSide (+1 left / -1 right of the centerline); the
 * world pose is DERIVED from the host wall so the device follows wall
 * moves. x/y keep the last baked position for unhosting.
 */
export type ObjectGeometryType = "point" | "linear" | "area";

export interface PlanObject {
  id: string;
  floorId: string;
  definitionId: string;
  geometryType: ObjectGeometryType;
  x: number;
  y: number;
  endX: number | null;
  endY: number | null;
  /** Rotation in tenths of a degree (point objects; 0 = footprint width along +x). */
  rotationX10: number;
  width: number;
  depth: number;
  heightInches: number | null;
  mountHeightInches: number | null;
  /** Countertop → its cabinet run; length/depth sync until detached. */
  parentObjectId: string | null;
  /** True once the user overrides a derived object (stops syncing to parent). */
  detached: boolean;
  hostWallId: string | null;
  hostOffset: number | null;
  hostSide: 1 | -1 | null;
  /** Explicit room pin; null = derive from containment. */
  roomId: string | null;
  /** Scope binding: the Project Feature this object contributes quantity to. */
  featureId: string | null;
  /**
   * Structured configuration, interpreted by the object's own definition
   * (spec §61D): a vanity's sink arrangement, a corner cabinet's shape, a
   * laundry pair's configuration. Null when there is nothing to configure.
   */
  config: string | null;
  originId: string | null;
  notes: string;
  archivedAt: string | null;
}

export interface RoomAnchor {
  roomId: string;
  floorId: string;
  x: number;
  y: number;
}

export interface PlanModel {
  /** The Design (scenario) this plan belongs to — Existing or a Proposed design. */
  designId: string;
  floorId: string;
  nodes: ReadonlyMap<string, PlanNode>;
  walls: ReadonlyMap<string, Wall>;
  openings: ReadonlyMap<string, WallOpening>;
  objects: ReadonlyMap<string, PlanObject>;
  anchors: ReadonlyMap<string, RoomAnchor>;
}

export const DEFAULT_WALL_HEIGHT_INCHES = 96;

export function emptyPlan(floorId: string, designId = "design-unassigned"): PlanModel {
  return { designId, floorId, nodes: new Map(), walls: new Map(), openings: new Map(), objects: new Map(), anchors: new Map() };
}

export function activeObjects(model: PlanModel): PlanObject[] {
  return [...model.objects.values()].filter((o) => o.archivedAt === null);
}

/** Physical construction walls only (excludes logical room boundaries). */
export function physicalWalls(model: PlanModel): Wall[] {
  return activeWalls(model).filter((w) => w.kind !== "boundary");
}

export function activeWalls(model: PlanModel): Wall[] {
  return [...model.walls.values()].filter((w) => w.archivedAt === null);
}

export function activeNodes(model: PlanModel): PlanNode[] {
  return [...model.nodes.values()].filter((n) => n.archivedAt === null);
}

export function activeOpenings(model: PlanModel): WallOpening[] {
  return [...model.openings.values()].filter((o) => o.archivedAt === null);
}

export function openingsOfWall(model: PlanModel, wallId: string): WallOpening[] {
  return activeOpenings(model).filter((o) => o.wallId === wallId);
}

/** Effective wall height: override → floor default → 96". */
export function effectiveWallHeightInches(wall: Pick<Wall, "heightInches">, floorDefaultInches: number | null): number {
  return wall.heightInches ?? floorDefaultInches ?? DEFAULT_WALL_HEIGHT_INCHES;
}

/** Required node lookup (throws for a dangling reference — a model bug). */
export function nodeOf(model: PlanModel, id: string): PlanNode {
  const n = model.nodes.get(id);
  if (!n) throw new Error(`Plan node ${id} not found`);
  return n;
}

export function wallOf(model: PlanModel, id: string): Wall {
  const w = model.walls.get(id);
  if (!w) throw new Error(`Wall ${id} not found`);
  return w;
}

export function wallEndpoints(model: PlanModel, wall: Wall): [PlanNode, PlanNode] {
  return [nodeOf(model, wall.startNodeId), nodeOf(model, wall.endNodeId)];
}
